import React, { useState, useEffect } from 'react';
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage';
import { SendMessage as BLSendMessage } from './../../../utilities/BL/Anecdotes';

// ==============================================================================
// CONFIGURACIÓN
// ==============================================================================
const COOLDOWN_STORAGE_KEY = "anecdotes_next_allowed_ts";
const DEFAULT_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutos

// Utilidad: Formatea ms → "m:ss"
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const Anecdotes = ({ cooldownMs = DEFAULT_COOLDOWN_MS }) => {
  const { showMessage } = useFloatMessage();

  // --- VISTAS ---
  const [view, setView] = useState("intro"); // 'intro' | 'form' | 'sent'

  // --- ESTADOS ---
  const [formData, setFormData] = useState({ content: '', sender: '' });
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // --- COOLDOWN ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Cargar cooldown al montar
  useEffect(() => {
    const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (saved) {
      const ts = parseInt(saved, 10);
      if (!Number.isNaN(ts)) setNextAllowedTs(ts);
    }
  }, []);

  // Timer del cooldown
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      setRemainingMs(Math.max(0, diff));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // --- HANDLERS ---
  const handleStart = () => {
    if (!canStartNew) {
      showMessage({ type: MESSAGE_TYPE.INFO, text: `Espera ${msToMinSec(remainingMs)} para enviar otra anécdota.` });
      return;
    }
    setView("form");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validación: Contenido (Máx 255)
    if (name === 'content' && value.length > 255) return;

    // Validación: Remitente (Máx 30)
    if (name === 'sender' && value.length > 30) return;

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verificación de Cooldown antes de enviar (prevención multi-pestaña / manipulación)
    const savedCooldown = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (savedCooldown) {
      const ts = parseInt(savedCooldown, 10);
      if (!Number.isNaN(ts) && Date.now() < ts) {
        const remaining = Math.max(0, ts - Date.now());
        const waitMsg = `Debes esperar ${msToMinSec(remaining)} antes de enviar otra anécdota.`;
        setErrorMsg(waitMsg);
        showMessage({ type: MESSAGE_TYPE.INFO, text: waitMsg });
        return;
      }
    }

    // Validación visual rápida
    if (!formData.content.trim()) {
      setErrorMsg("Escribe tu anécdota");
      return;
    }

    setIsSending(true);
    setErrorMsg(null);

    try {
      const response = await BLSendMessage(formData);

      if (response.success) {
        // Activar Cooldown
        const ts = Date.now() + cooldownMs;
        setNextAllowedTs(ts);
        localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts));

        // Limpieza de campos
        setFormData({ content: '', sender: '' });

        // Cambiar a vista de enviado
        setView("sent");
      } else {
        setErrorMsg(response.message);
        showMessage({ type: MESSAGE_TYPE.BAD, text: response.message });
      }

    } catch (err) {
      console.error(err);
      setErrorMsg("Error inesperado. Intenta de nuevo.");
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Ocurrió un error inesperado." });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="container SelfieFan Anecdotes__Fan">

      {/* VISTA 1: INTRODUCCIÓN */}
      {view === "intro" && (
        <div className="SelfieFan__Intro container pt-4">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="SelfieFan__Fan__Title">TU MEJOR ANÉCDOTA</p>
          <p className="SelfieFan__Fan__Text--Center">¡Comparte tus momentos inolvidables con nosotros!</p>

          <ol className="SelfieFan__List">
            <li>Escribe tu anécdota</li>
            <li>Escribe tu nombre (opcional)</li>
            <li>Da clic en 'Enviar mi anécdota'</li>
          </ol>

          <p className='Anecdotes__Fan__Text--Center'>Tu anécdota podría aparecer en las pantallas del Estadio Libertad Financiera</p>

          <div className="SelfieFan__Intro__Actions">
            <button className="Btn__Adsl--Red" onClick={handleStart} disabled={!canStartNew} title={!canStartNew ? `Espera ${msToMinSec(remainingMs)}` : ""}>
              {!canStartNew ? `Puedes enviar otra en ${msToMinSec(remainingMs)}` : <><i className='bi bi-chat-quote'></i> Comenzar</>}
            </button>
          </div>
        </div>
      )}

      {/* VISTA 2: FORMULARIO DE CAPTURA */}
      {view === "form" && (
        <div className="Anecdotes__Fan__FormContainer pt-4">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />

          <p className="Anecdotes__Fan__Text--Title Anecdotes__Fan__Text--Center Anecdotes__Fan__Element--SpaceUp">Comparte tu anécdota</p>

          <form className='Anecdotes__Fan__FormContainer__Form' onSubmit={handleSubmit}>

            {/* Campo Anécdota */}
            <label className='Anecdotes__Fan__FormContainer__Form--SpaceUp'>Escribe tu anécdota</label>
            <textarea
              className="form-control"
              rows="4"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Escribe tu anécdota aquí..."
              maxLength={255}
              disabled={isSending}
            ></textarea>
            <p className="text-end small">{formData.content.length}/255</p>

            {/* Campo Remitente */}
            <label className='Anecdotes__Fan__FormContainer__Form--SpaceUp'>¿Quién envía la anécdota? (opcional)</label>
            <input
              type="text"
              className="form-control"
              name="sender"
              value={formData.sender}
              onChange={handleChange}
              placeholder="Escribe tu nombre"
              maxLength={30}
              disabled={isSending}
            />
            <div className="text-end small">{formData.sender.length}/30</div>

            {/* Botones de Acción */}
            <button
              type="submit"
              className="Btn__Adsl--Red Anecdotes__Fan__FormContainer__Form--SpaceUp"
              disabled={isSending || !formData.content.trim()}
            >
              {isSending ? "Enviando..." : <><i className='bi bi-chat-quote'></i> Enviar mi anécdota</>}
            </button>
            <button
              type="button"
              className="Btn__Adsl--RedOutline Anecdotes__Fan__FormContainer__Form--SpaceUp"
              onClick={() => setView("intro")}
              disabled={isSending}
            >
              Salir
            </button>

            {/* Mensajes de Error */}
            {errorMsg && (<p className="Anecdotes__Fan__Text--Center text-danger mt-3 fw-bold">{errorMsg}</p>)}
          </form>
        </div>
      )}

      {/* VISTA 3: CONFIRMACIÓN Y COOLDOWN */}
      {view === "sent" && (
        <div className="SelfieFan__Sent container">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
          <p className="SelfieFan__Sent__Title">¡Anécdota enviada!</p>
          <p className='Anecdotes__Fan__Text--Center'>Tu anécdota ha sido enviada correctamente</p>

          <div className="SelfieFan__Sent__Actions justify-content-center">
            <button
              className="Btn__Adsl--RedOutline"
              onClick={() => canStartNew && setView("intro")}
              disabled={!canStartNew}
            >
              {canStartNew ? <><i className='bi bi-chat-quote'></i> Enviar otra anécdota</> : `Puedes enviar otra en ${msToMinSec(remainingMs)}`}
            </button>
          </div>

          <hr />

          {/* Sección Inferior 1: Publicidad Interaktia */}
          <a
            href='https://interaktia.com/?utm_source=anecdote_web&utm_medium=banner&utm_campaign=fdm_adsl'
            target='_blank'
            rel="noreferrer"
            className='SelfieFan__Sent__Banner'
          >
            <img
              src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif'
              alt='Publicidad'
              className='d-block m-auto SelfieFan__Sent__Banner__Image'
            />
          </a>
          <a
            href='https://interaktia.com/?utm_source=anecdote_web&utm_medium=banner&utm_campaign=fdm_adsl'
            target='_blank'
            rel="noreferrer"
            className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'
          >
            Contactar ahora
          </a>

          <hr />

          {/* Sección Inferior 2: Bloque Tecnología y Contacto */}
          <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
          <p className='text-center mb-0'>Interaktia está listo para ti</p>
          <a
            href='https://interaktia.com/?utm_source=anecdote_web&utm_medium=banner_text&utm_campaign=fdm_adsl'
            target='_blank'
            rel="noreferrer"
            className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'
          >
            Contactar ahora
          </a>
        </div>
      )}

      {/* Footer de Créditos */}
      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=anecdote_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
      </footer>
    </div>
  );
};

export { Anecdotes };
